import { useTranslation } from "react-i18next";
import {
  basaDocumentRoutePath,
  BasaDocumentErrorResponse,
  parseBasaDocumentLink,
} from "@t3tools/contracts";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";

import { CheckIcon, CopyIcon, XIcon } from "lucide-react";
import {
  type AnchorHTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { primaryEnvironmentFetch } from "../../lib/runtime";
import { resolvePrimaryEnvironmentHttpUrl } from "../../environments/primary/target";
import { useBasaDocumentDialogStore } from "../../basaDocumentDialogStore";
import { Button } from "../ui/button";
import { Dialog, DialogClose, DialogPopup, DialogTitle } from "../ui/dialog";
import {
  findQuoteRangesInSegments,
  type SegmentRange,
  type TextSegment,
} from "./BasaDocumentDialog.logic";

const QUOTE_HIGHLIGHT = "basa-quote";
const BLOCK_SELECTOR = "div, p, td, li";
const decodeDocumentError = Schema.decodeUnknownOption(BasaDocumentErrorResponse);

interface RenderedDocument {
  readonly title: string;
  readonly elements: ReadonlyArray<HTMLElement>;
}

type FailureReason = BasaDocumentErrorResponse["error"] | "session_expired" | "render_failed";

type LoadState =
  | { readonly status: "loading" }
  | { readonly status: "failed"; readonly reason: FailureReason }
  | { readonly status: "ready"; readonly document: RenderedDocument };

function highlightRegistry(): HighlightRegistry | null {
  return typeof CSS !== "undefined" && "highlights" in CSS ? CSS.highlights : null;
}

const fontSizeOf = (element: Element | null): string | null =>
  element instanceof HTMLElement && Number.parseFloat(element.style.fontSize) >= 1
    ? element.style.fontSize
    : null;

function restoreCollapsedFontSizes(root: HTMLElement) {
  for (const span of root.querySelectorAll<HTMLElement>("span")) {
    if (!span.style.fontSize || fontSizeOf(span) || !span.textContent?.trim()) continue;
    let previous = span.previousElementSibling;
    while (previous && !fontSizeOf(previous)) previous = previous.previousElementSibling;
    const size = fontSizeOf(previous);
    if (size) span.style.fontSize = size;
    else span.style.removeProperty("font-size");
  }
}

async function renderRtf(buffer: ArrayBuffer): Promise<RenderedDocument> {
  const { RTFJS } = await import("rtf.js");
  RTFJS.loggingEnabled(false);
  const document = new RTFJS.Document(buffer, {
    onPicture: (isLegacy, create) => {
      if (isLegacy) return window.document.createElement("span");
      try {
        return create();
      } catch {
        return window.document.createElement("span");
      }
    },
  });
  const title = document.metadata().title;
  const elements = await document.render();
  for (const element of elements) {
    restoreCollapsedFontSizes(element);
    for (const node of [element, ...element.querySelectorAll<HTMLElement>("*")]) {
      node.style.removeProperty("color");
      node.style.removeProperty("background-color");
    }
    for (const link of element.querySelectorAll("a")) {
      if (!/^https?:\/\//i.test(link.getAttribute("href") ?? "")) {
        link.replaceWith(...link.childNodes);
        continue;
      }
      link.target = "_blank";
      link.rel = "noopener noreferrer";
    }
  }
  return { title: typeof title === "string" ? title.trim() : "", elements };
}

function collectCardQuotes(anchor: HTMLElement): string[] {
  const root = anchor.closest(".chat-markdown");
  let block: Element = anchor;
  while (block.parentElement && block.parentElement !== root) {
    block = block.parentElement;
  }
  if (!root || block.parentElement !== root) return [];
  const quotes: string[] = [];
  for (
    let sibling = block.previousElementSibling;
    sibling && sibling.tagName !== "HR";
    sibling = sibling.previousElementSibling
  ) {
    if (sibling.tagName !== "BLOCKQUOTE") continue;
    const paragraphs = [...sibling.querySelectorAll("p")].map((p) => p.textContent ?? "");
    quotes.unshift(...(paragraphs.length > 0 ? paragraphs : [sibling.textContent ?? ""]));
  }
  return quotes.filter((quote) => quote.trim().length > 0);
}

function textSegments(root: HTMLElement): { nodes: Text[]; segments: TextSegment[] } {
  const nodes: Text[] = [];
  const segments: TextSegment[] = [];
  let previousBlock: Element | null = null;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!(node instanceof Text) || node.length === 0) continue;
    const block = node.parentElement?.closest(BLOCK_SELECTOR) ?? null;
    nodes.push(node);
    segments.push({ text: node.data, startsBlock: block !== previousBlock });
    previousBlock = block;
  }
  return { nodes, segments };
}

function toDomRange(nodes: ReadonlyArray<Text>, range: SegmentRange): Range | null {
  const start = nodes[range.start.segment];
  const end = nodes[range.end.segment];
  if (!start || !end) return null;
  const domRange = document.createRange();
  domRange.setStart(start, Math.min(range.start.offset, start.length));
  domRange.setEnd(end, Math.min(range.end.offset, end.length));
  return domRange;
}

function BasaDocumentView({
  href,
  quotes,
}: {
  readonly href: string;
  readonly quotes: ReadonlyArray<string>;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [copied, setCopied] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    const load = async () => {
      const address = parseBasaDocumentLink(href);
      if (!address) {
        setState({ status: "failed", reason: "invalid_address" });
        return;
      }
      let buffer: ArrayBuffer;
      try {
        const url = new URL(
          basaDocumentRoutePath(address, "rtf"),
          resolvePrimaryEnvironmentHttpUrl("/"),
        );
        const response = await primaryEnvironmentFetch(url.href, { signal: controller.signal });
        if (!response.ok) {
          const error = decodeDocumentError(await response.json().catch(() => null));
          if (!controller.signal.aborted) {
            setState({
              status: "failed",
              reason: Option.isSome(error)
                ? error.value.error
                : response.status === 401
                  ? "session_expired"
                  : "unavailable",
            });
          }
          return;
        }
        buffer = await response.arrayBuffer();
      } catch {
        if (!controller.signal.aborted) setState({ status: "failed", reason: "unavailable" });
        return;
      }
      if (controller.signal.aborted) return;
      try {
        const document = await renderRtf(buffer);
        if (!controller.signal.aborted) setState({ status: "ready", document });
      } catch {
        if (!controller.signal.aborted) setState({ status: "failed", reason: "render_failed" });
      }
    };
    void load();
    return () => controller.abort();
  }, [href, attempt]);

  useLayoutEffect(() => {
    const root = contentRef.current;
    if (state.status !== "ready" || !root) return;
    root.replaceChildren(...state.document.elements);
    const { nodes, segments } = textSegments(root);
    const ranges = findQuoteRangesInSegments(segments, quotes).flatMap((range) => {
      const domRange = toDomRange(nodes, range);
      return domRange ? [domRange] : [];
    });
    const registry = highlightRegistry();
    if (registry && ranges.length > 0) registry.set(QUOTE_HIGHLIGHT, new Highlight(...ranges));
    ranges[0]?.startContainer.parentElement?.scrollIntoView({ block: "center" });
    return () => {
      registry?.delete(QUOTE_HIGHLIGHT);
    };
  }, [state, quotes]);

  const loaded = state.status === "ready" ? state.document : null;

  const copyText = () => {
    const text = contentRef.current?.innerText;
    if (!text) return;
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => setCopied(false));
  };

  const failureMessages = {
    not_configured: t("chat/theLegalDatabaseIs_if3pfb"),
    unavailable: t("chat/theLegalDatabaseIs_yn5wy8"),
    not_found: t("chat/theDocumentWasNot_y2hw6j"),
    invalid_address: t("chat/theDocumentAddressIs_1lhinn"),
    session_expired: t("chat/yourSessionHasExpired_rbnesu"),
    render_failed: t("chat/couldNotDisplayThe_u64ykd"),
  } satisfies Record<FailureReason, string>;

  return (
    <>
      <div className="flex items-start gap-3 border-b px-5 py-3">
        <DialogTitle className="line-clamp-2 min-w-0 flex-1 font-sans text-sm leading-snug font-semibold">
          {loaded?.title || t("chat/fullText_e23tw5")}
        </DialogTitle>
        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label={t("chat/copyText_lc08ee")}
            disabled={!loaded}
            onClick={copyText}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </Button>
          <DialogClose
            aria-label={t("common/close_uwgpf5")}
            render={<Button size="icon-sm" variant="ghost" />}
          >
            <XIcon />
          </DialogClose>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        {state.status === "loading" ? (
          <p className="py-16 text-center text-sm text-muted-foreground">
            {t("settings/loading_gbm5n1")}
          </p>
        ) : null}
        {state.status === "failed" ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center text-sm text-muted-foreground">
            <p>{failureMessages[state.reason]}</p>
            <Button size="sm" variant="outline" onClick={() => setAttempt((value) => value + 1)}>
              {t("common/retry_9llvl9")}
            </Button>
          </div>
        ) : null}
        <div
          ref={contentRef}
          hidden={!loaded}
          className="mx-auto max-w-[46rem] text-[12pt] text-foreground [&_a]:text-primary [&_a]:underline"
        />
      </div>
    </>
  );
}

export function BasaDocumentLink({
  href,
  children,
  onClick,
  ...anchorProps
}: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  readonly href: string;
  readonly children?: ReactNode;
}) {
  const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    event.preventDefault();
    useBasaDocumentDialogStore
      .getState()
      .open({ href, quotes: collectCardQuotes(event.currentTarget) });
  };

  return (
    <a
      {...anchorProps}
      href={href}
      onClick={handleClick}
      onAuxClick={(event) => {
        if (event.button === 1) handleClick(event);
      }}
    >
      {children}
    </a>
  );
}

export function BasaDocumentDialog() {
  const request = useBasaDocumentDialogStore((state) => state.request);
  const close = useBasaDocumentDialogStore((state) => state.close);

  return (
    <Dialog
      open={request !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogPopup className="h-[min(88vh,60rem)] max-w-3xl" showCloseButton={false}>
        {request ? (
          <BasaDocumentView key={request.href} href={request.href} quotes={request.quotes} />
        ) : null}
      </DialogPopup>
    </Dialog>
  );
}
